import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  ClockIcon, 
  GlobeAltIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/api';
import { formatDateComparison } from '../../utils/analysisHelpers';

const AnalysisHistoryList = ({
  onRunAgain,
  onActionComplete,
  shouldFetch = false,
  isDemoUser = false,
  darkMode = false,
  refreshTrigger = 0,
  isAnalysisRunning = false
}) => {
  const navigate = useNavigate();
  
  // Internal state
  const [historyList, setHistoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  
  // UI state
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [uniqueProperties, setUniqueProperties] = useState([]);
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(null);
  
  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'Just now';
  };

  // Generate condensed description components for history entry
  const generateDescriptionComponents = (historyItem) => {
    const domain = historyItem.property; // Use property directly as stored by Search Console
    
    // Use the new smart date formatting
    const dateComparison = formatDateComparison(
      historyItem.period_1, 
      historyItem.period_2,
      { includeLabels: false, shortYear: true }
    );
    
    return {
      domain,
      dateComparison: dateComparison.text,
      period1Text: dateComparison.period1Text,
      period2Text: dateComparison.period2Text,
      separator: dateComparison.separator
    };
  };

  // Fetch history (using real API endpoint)
  const fetchHistory = useCallback(async () => {
    if (isDemoUser) {
      setHistoryList([]);
      setHasFetched(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Simple API call without query parameters - frontend handles filtering
      const response = await apiRequest('/api/analyze/history/list/', {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Use the history array from the API response
      setHistoryList(data.history || []);
      setHasFetched(true);
      
    } catch (error) {
      setHistoryList([]);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [isDemoUser]);

  // Fetch when shouldFetch becomes true
  useEffect(() => {
    if (shouldFetch && !hasFetched) {
      fetchHistory();
    }
  }, [shouldFetch, hasFetched, fetchHistory]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && hasFetched) {
      fetchHistory();
    }
  }, [refreshTrigger, hasFetched, fetchHistory]);

  // Update unique properties and countries when historyList changes
  useEffect(() => {
    const properties = [...new Set(historyList.map(item => item.property))];
    // Extract countries from the data, defaulting to 'Global' if not specified
    const countries = [...new Set(historyList.map(item => item.country || 'Global'))];
    setUniqueProperties(properties);
    setUniqueCountries(countries);
  }, [historyList]);

  // Reset carousel when filters change
  useEffect(() => {
    setCurrentIndex(0);
  }, [propertyFilter, countryFilter]);

  // Handle "Run Again" action
  const handleRunAgain = (historyItem) => {
    // Call the callback to let parent handle the analysis execution
    if (onRunAgain) {
      onRunAgain(historyItem);
    }
    
    // Close the sidebar after starting the analysis
    if (onActionComplete) {
      onActionComplete();
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-3">
        <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${darkMode ? 'border-blue-400' : 'border-blue-600'}`}></div>
        <span className="ml-2 text-xs">Loading history...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`p-4 rounded-lg border ${darkMode ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-800'}`}>
        <div className="flex items-center justify-between">
          <span>Error loading history: {error}</span>
          <button
            onClick={fetchHistory}
            className={`ml-2 px-2 py-1 text-xs rounded ${darkMode ? 'bg-red-800 hover:bg-red-700' : 'bg-red-100 hover:bg-red-200'}`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  if (hasFetched && !historyList.length) {
    return (
      <div className={`p-3 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <ClockIcon className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-xs">No analysis history yet</p>
        <p className="text-xs mt-1 opacity-75">Analyses will appear here automatically</p>
        <button
          onClick={fetchHistory}
          className={`mt-2 px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
        >
          Refresh
        </button>
      </div>
    );
  }

  // Show nothing if we haven't been asked to fetch yet
  if (!shouldFetch && !hasFetched) {
    return null;
  }

  // Filter history on frontend
  const filteredHistory = historyList.filter(item => {
    const propertyMatch = propertyFilter === 'all' || item.property === propertyFilter;
    const itemCountry = item.country || 'Global'; // Default to 'Global' if no country specified
    const countryMatch = countryFilter === 'all' || itemCountry === countryFilter;
    return propertyMatch && countryMatch;
  });

  // Carousel navigation
  const goToPrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - itemsPerPage));
  };

  const goToNext = () => {
    setCurrentIndex(prev => Math.min(filteredHistory.length - itemsPerPage, prev + itemsPerPage));
  };

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex + itemsPerPage < filteredHistory.length;

  // Get current page items
  const currentItems = filteredHistory.slice(currentIndex, currentIndex + itemsPerPage);

  // Show empty filtered results
  if (hasFetched && filteredHistory.length === 0) {
    return (
      <div className={`p-3 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <p className="text-xs">No analyses match your filters</p>
        <button
          onClick={() => {
            setPropertyFilter('all');
            setCountryFilter('all');
          }}
          className={`mt-2 px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
        >
          Clear Filters
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Condensed Filters */}
      <div className="space-y-2">
        {/* Property Filter */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Property
          </label>
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className={`w-full rounded border shadow-sm text-xs py-1
              ${darkMode 
                ? 'bg-gray-800 border-gray-700 text-gray-200' 
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            <option value="all">All Properties</option>
            {uniqueProperties.map(property => (
              <option key={property} value={property}>
                {property}
              </option>
            ))}
          </select>
        </div>

        {/* Country Filter */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Country
          </label>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className={`w-full rounded border shadow-sm text-xs py-1
              ${darkMode 
                ? 'bg-gray-800 border-gray-700 text-gray-200' 
                : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            <option value="all">All Countries</option>
            {uniqueCountries.map(country => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* History List with Carousel */}
      <div className="space-y-2">
        {/* Carousel Header */}
        {filteredHistory.length > itemsPerPage && (
          <div className="flex items-center justify-between">
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {currentIndex + 1}-{Math.min(currentIndex + itemsPerPage, filteredHistory.length)} of {filteredHistory.length}
            </span>
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPrevious}
                disabled={!canGoPrevious}
                className={`p-1 rounded ${
                  canGoPrevious
                    ? darkMode 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    : 'opacity-30 cursor-not-allowed text-gray-400'
                }`}
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                onClick={goToNext}
                disabled={!canGoNext}
                className={`p-1 rounded ${
                  canGoNext
                    ? darkMode 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    : 'opacity-30 cursor-not-allowed text-gray-400'
                }`}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Carousel Items */}
        <div className={`divide-y border rounded-lg
          ${darkMode ? 'divide-gray-700 border-gray-700 bg-gray-900' : 'divide-gray-200 border-gray-200 bg-white'}`}
        >
          {currentItems.map((historyItem, index) => {
            const { domain, dateComparison, period1Text, period2Text, separator } = generateDescriptionComponents(historyItem);
            
            return (
              <div 
                key={`${historyItem.property}-${historyItem.period_1.start}-${historyItem.period_2.start}-${index}`}
                className={`transition duration-150 ease-in-out
                  ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
              >
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Domain pill */}
                      <div className="mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                          ${darkMode 
                            ? 'bg-blue-900/50 text-blue-200 border border-blue-700/50' 
                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {domain}
                        </span>
                      </div>
                      
                      {/* Color-coded date comparison */}
                      <div className={`text-sm mb-1 flex items-center flex-wrap gap-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span className={`${darkMode ? 'text-blue-300' : 'text-blue-600'} font-medium`}>
                          {period1Text}
                        </span>
                        <span className={`mx-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          vs
                        </span>
                        <span className={`${darkMode ? 'text-purple-300' : 'text-purple-600'} font-medium`}>
                          {period2Text}
                        </span>
                      </div>
                      
                      {/* Condensed meta information - single line */}
                      <div className={`text-xs flex items-center space-x-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="flex items-center space-x-1">
                          <ClockIcon className="w-3 h-3" />
                          <span>{formatRelativeTime(historyItem.last_run)}</span>
                        </span>
                        {historyItem.run_count > 1 && (
                          <span className="flex items-center space-x-1">
                            <ArrowPathIcon className="w-3 h-3" />
                            <span>{historyItem.run_count}x</span>
                          </span>
                        )}
                        {(historyItem.country && historyItem.country !== 'Global') && (
                          <span className="flex items-center space-x-1">
                            <GlobeAltIcon className="w-3 h-3" />
                            <span>
                              {historyItem.country}
                              {historyItem.use_country_scaling && (
                                <span className={`ml-1 text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                  (scaled)
                                </span>
                              )}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Condensed notes preview */}
                      {historyItem.notes && (
                        <div className={`mt-1 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="truncate block">
                            {historyItem.notes.length > 50 
                              ? `${historyItem.notes.substring(0, 50)}...` 
                              : historyItem.notes
                            }
                          </span>
                          {historyItem.notes.length > 50 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedHistory(expandedHistory === (currentIndex + index) ? null : (currentIndex + index));
                              }}
                              className={`text-xs mt-1 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                            >
                              {expandedHistory === (currentIndex + index) ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Condensed Run Again Button */}
                    <button
                      onClick={() => !isAnalysisRunning && handleRunAgain(historyItem)}
                      disabled={isAnalysisRunning}
                      className={`ml-2 flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded transition-colors
                        ${isAnalysisRunning
                          ? darkMode 
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : darkMode 
                            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      title={isAnalysisRunning ? "Analysis in progress..." : "Run this analysis again"}
                    >
                      <PlayIcon className="w-3 h-3" />
                      <span>{isAnalysisRunning ? 'Running...' : 'Run'}</span>
                    </button>
                  </div>
                </div>

                {/* Expanded Notes */}
                {expandedHistory === (currentIndex + index) && historyItem.notes && (
                  <div 
                    className={`px-3 pb-3 text-xs border-t
                      ${darkMode 
                        ? 'border-gray-700 text-gray-300' 
                        : 'border-gray-200 text-gray-600'
                      }`}
                  >
                    <div className="mt-2 whitespace-pre-wrap">{historyItem.notes}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(AnalysisHistoryList); 